import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon, CalendarIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import Button from "./ui/Button";
import LateBookingModal from "./LateBookingModal";

interface DateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (date: Date) => void;
  suggestedDate?: Date;
  excludeDates?: Date[];
}

const DateSelectionModal: React.FC<DateSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  suggestedDate,
  excludeDates = [],
}) => {
  const [showLateBookingModal, setShowLateBookingModal] = useState(false);

  const excluded = useMemo(
    () =>
      (excludeDates || []).map((d) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(),
      ),
    [excludeDates],
  );

  // Calculate the first valid booking date (Today + 2 days)
  const validBookingStartDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const getFirstAvailableFrom = (start: Date) => {
    const d = new Date(start);
    // normalize to midnight
    d.setHours(0, 0, 0, 0);

    // If the start date is before our valid booking window, bump it up
    if (d < validBookingStartDate) {
      return new Date(validBookingStartDate);
    }

    // find the next date not excluded
    while (excluded.includes(d.getTime())) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  };

  const initial = useMemo(
    () => (suggestedDate ? getFirstAvailableFrom(suggestedDate) : null),
    [suggestedDate, excluded, validBookingStartDate],
  );

  const [selectedDate, setSelectedDate] = useState<Date | null>(initial);

  useEffect(() => {
    setSelectedDate(initial);
  }, [initial]);

  const handleDateChange = (date: Date | null) => {
    if (!date) return;

    // Check if the selected date is too early (Today or Tomorrow)
    if (date < validBookingStartDate) {
      setShowLateBookingModal(true);
      return; // Do not update selectedDate
    }

    setSelectedDate(date);
  };

  const handleConfirm = () => {
    if (selectedDate) {
      onConfirm(selectedDate);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <div className="flex items-center space-x-3">
                  <CalendarIcon className="h-6 w-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">
                    Select Lesson Date
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Lesson <span className="text-red-500">*</span>
                  </label>
                  <DatePicker
                    selected={selectedDate}
                    onChange={handleDateChange}
                    dateFormat="yyyy-MM-dd (EEEE)"
                    minDate={new Date()} // Allow clicking today/tomorrow to trigger modal
                    excludeDates={excludeDates}
                    className="w-[14rem] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholderText="Select date"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Online bookings must be made at least 2 days in advance.
                  </p>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    className="flex-1"
                    disabled={!selectedDate}
                  >
                    Proceed
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <LateBookingModal
            isOpen={showLateBookingModal}
            onClose={() => setShowLateBookingModal(false)}
          />
        </>
      )}
    </AnimatePresence>
  );
};

export default DateSelectionModal;
